// src/components/ecommerce/PaymentStep.jsx
import React from 'react';
import { CreditCard, Receipt, Truck, Building, Shield, AlertCircle } from 'lucide-react';

export const PaymentStep = ({ orderData, onUpdate, storeSettings, orderSummary }) => {
  const handlePaymentMethodChange = (method, details = {}) => {
    onUpdate('payment', { method, details });
  };

  const paymentMethods = [];

  // PayFast
  if (storeSettings.paymentMethods?.payfast?.enabled) {
    paymentMethods.push({
      id: 'payfast',
      name: 'Credit/Debit Card',
      description: 'Pay securely with your card via PayFast',
      icon: CreditCard,
      color: 'blue'
    });
  }

  // EFT
  if (storeSettings.paymentMethods?.eft?.enabled) {
    paymentMethods.push({
      id: 'eft',
      name: 'Electronic Funds Transfer',
      description: 'Bank transfer payment',
      icon: Building,
      color: 'green'
    });
  }

  // Cash on Delivery
  if (storeSettings.paymentMethods?.cod?.enabled) {
    const codFee = storeSettings.paymentMethods.cod.fee || 0;
    paymentMethods.push({
      id: 'cod',
      name: 'Pay on Delivery',
      description: `Cash or card payment on delivery${codFee > 0 ? ` (+R${codFee})` : ''}`,
      icon: Truck,
      color: 'orange'
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Method</h3>
        <p className="text-gray-600">Choose how you'd like to pay for your order.</p>
      </div>

      {paymentMethods.length === 0 ? (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No payment methods configured</p>
        </div>
      ) : (
        <div className="space-y-4">
          {paymentMethods.map((method) => {
            const Icon = method.icon;
            const isSelected = orderData.payment.method === method.id;
            
            return (
              <div
                key={method.id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  isSelected
                    ? `border-${method.color}-500 bg-${method.color}-50`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handlePaymentMethodChange(method.id)}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    checked={isSelected}
                    onChange={() => handlePaymentMethodChange(method.id)}
                    className={`h-4 w-4 text-${method.color}-600 focus:ring-${method.color}-500`}
                  />
                  <Icon className={`h-6 w-6 text-${method.color}-600`} />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{method.name}</h4>
                    <p className="text-sm text-gray-600">{method.description}</p>
                  </div>
                  <Shield className="h-5 w-5 text-gray-400" />
                </div>

                {/* Payment Method Details */}
                {isSelected && method.id === 'eft' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-3">Bank Transfer Details:</h5>
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-gray-600">Bank:</span>
                        <span className="font-medium">{storeSettings.paymentMethods.eft.bankName}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-gray-600">Account Holder:</span>
                        <span className="font-medium">{storeSettings.paymentMethods.eft.accountHolder}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-gray-600">Account Number:</span>
                        <span className="font-medium">{storeSettings.paymentMethods.eft.accountNumber}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-gray-600">Branch Code:</span>
                        <span className="font-medium">{storeSettings.paymentMethods.eft.branchCode}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-gray-600">Reference:</span>
                        <span className="font-medium">Use order number</span>
                      </div>
                    </div>
                    {storeSettings.paymentMethods.eft.instructions && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">{storeSettings.paymentMethods.eft.instructions}</p>
                      </div>
                    )}
                  </div>
                )}

                {isSelected && method.id === 'cod' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-3">Payment on Delivery:</h5>
                    <div className="space-y-2">
                      {storeSettings.paymentMethods.cod.acceptCash && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-gray-700">Cash payment accepted</span>
                        </div>
                      )}
                      {storeSettings.paymentMethods.cod.acceptCard && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-gray-700">Card payment accepted</span>
                        </div>
                      )}
                    </div>
                    {storeSettings.paymentMethods.cod.fee > 0 && (
                      <div className="mt-2 p-2 bg-yellow-50 rounded">
                        <p className="text-sm text-yellow-800">
                          Additional R{storeSettings.paymentMethods.cod.fee} COD fee applies
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Security Notice */}
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-5 w-5 text-green-600" />
          <h4 className="font-medium text-green-900">Secure Payment</h4>
        </div>
        <p className="text-sm text-green-800">
          Your payment information is encrypted and secure. We never store your payment details.
        </p>
      </div>
    </div>
  );
};